import type { RequestOptions } from '@@/plugin-request/request';
import type { RequestConfig } from '@umijs/max';
import { message, notification } from 'antd';
import { getLocale } from '@umijs/max';

// 错误处理方案： 错误类型
enum ErrorShowType {
  SILENT = 0,
  WARN_MESSAGE = 1,
  ERROR_MESSAGE = 2,
  NOTIFICATION = 3,
  REDIRECT = 9,
}
// 与后端约定的响应数据格式
interface ResponseStructure {
  success: boolean;
  data: any;
  errorCode?: number;
  errorMessage?: string;
  showType?: ErrorShowType;
}


// 显示错误信息（防重复）
const showErrorMessage = (content: string) => {
  message.error({
    content,
    duration: 3,
  });
};

/**
 * @name 错误处理
 * pro 自带的错误处理， 可以在这里做自己的改动
 * @doc https://umijs.org/docs/max/request#配置
 */
export const errorConfig: RequestConfig = {
  // 错误处理： umi@3 的错误处理方案。
  errorConfig: {
    // 错误抛出
    errorThrower: (res) => {
      const { success, data, errorCode, errorMessage, showType } =
        res as unknown as ResponseStructure;
      if (!success) {
        const error: any = new Error(errorMessage);
        error.name = 'BizError';
        error.info = { errorCode, errorMessage, showType, data };
        throw error; // 抛出自制的错误
      }
    },
    // 错误接收及处理
    errorHandler: (error: any, opts: any) => {
      if (opts?.skipErrorHandler) throw error;
      // 我们的 errorThrower 抛出的错误。
      if (error.name === 'BizError') {
        const errorInfo: ResponseStructure | undefined = error.info;
        if (errorInfo) {
          const { errorMessage, errorCode } = errorInfo;
          switch (errorInfo.showType) {
            case ErrorShowType.SILENT:
              // do nothing
              break;
            case ErrorShowType.WARN_MESSAGE:
              message.warning(errorMessage);
              break;
            case ErrorShowType.ERROR_MESSAGE:
              showErrorMessage(errorMessage || '操作失败');
              break;
            case ErrorShowType.NOTIFICATION:
              notification.open({
                description: errorMessage,
                message: errorCode,
              });
              break;
            case ErrorShowType.REDIRECT:
              // TODO: redirect
              break;
            default:
              showErrorMessage(errorMessage || '操作失败');
          }
        }
      } else if (error.response) {
        // Axios 的错误
        // 请求成功发出且服务器也响应了状态码，但状态代码超出了 2xx 的范围
        const { data, status } = error.response;

        // 优先显示服务器返回的错误信息
        if (data && data.message) {
          showErrorMessage(data.message);
        } else {
          // 根据HTTP状态码显示默认错误信息
          const statusMessages: Record<number, string> = {
            400: '请求参数错误',
            401: '未授权，请重新登录',
            403: '拒绝访问',
            404: '请求的资源不存在',
            500: '服务器错误，请稍后重试',
            502: '网关错误',
            503: '服务不可用',
            504: '网关超时',
          };
          showErrorMessage(statusMessages[status] || `请求失败 (${status})`);
        }
      } else if (error.request) {
        // 请求已经成功发起，但没有收到响应
        // \`error.request\` 在浏览器中是 XMLHttpRequest 的实例，
        // 而在node.js中是 http.ClientRequest 的实���
        showErrorMessage('网络异常，请检查网络连接');
      } else {
        // 发送请求时出了点问题
        showErrorMessage(error.message || '请求错误，请重试');
      }
    },
  },

  // 请求拦截器：统一添加认证 token 和国际化语言标识
  requestInterceptors: [
    (config: RequestOptions) => {
      // 1. 从 localStorage 获取 token 并添加到请求头
      const token = localStorage.getItem('auth_token');

      // 2. 获取当前用户选择的语言
      const locale = getLocale();

      config.headers = {
        ...config.headers,
        // 添加认证 token
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        // 添加国际化语言标识，用于后端国际化
        // 支持的语言：zh-CN（简体中文，默认）、en-US（英文）
        'Accept-Language': locale || 'zh-CN',
      };

      return config;
    },
  ],

  // 响应拦截器
  responseInterceptors: [
    (response) => {
      // 拦截响应数据，进行个性化处理
      const { data } = response as any;

      // 处理新的后端返回格式 Result<T>
      if (data && typeof data.code !== 'undefined') {
        // 401 未登录或 token 失效（需要特殊处理：清除登录状态并跳转）
        if (data.code === 401) {
          showErrorMessage('登录已过期，请重新登录');
          localStorage.clear();
          // 延迟跳转，确保用户能看到提示
          setTimeout(() => {
            window.location.href = '/user/login';
          }, 1000);
          return response;
        }

        // 处理其他错误码（403、500等）
        if (data.code !== 200) {
          showErrorMessage(data.message || `请求失败 (${data.code})`);
        }
      }

      return response;
    },
  ],
};
